import { take } from '../vfx-budget';
import { spawnQuad, spawnSprite } from '../vfx-pool';
import { texNeedle, texPolyBand, texPolyFill } from '../vfx-tex2';
import { FLAT, rnd, yawOf } from './util';
/* ============================================================
   風格 D：硬邊能量
   ============================================================

   ## 一句話

   冷色的能量武器，但**每一片都有銳利邊界**。

   ## 為什麼敢再用冷色

   前兩版被否掉的原話是「泛光太重，像魔法攻擊」。從那之後冷色被整批禁掉，
   但**冷色不是病因，沒有邊界才是**：模糊的光暈不管填什麼顏色都是魔法，
   而一片有明確輪廓的東西不管什麼顏色都讀成「物」。

   所以這個方向的每一筆都是切出來的：兩端收尖的針片、六角切面、
   有厚度的多邊形帶。加法混色只用在最小的核心上，其餘一律一般混色。

   ## 三條規則

   1. **針片取代火星。** 火星是圓的、會飄；針片是尖的、走直線、瞬間消失。
      同樣是「濺開」，一個讀成火藥一個讀成能量。
   2. **加法混色的量壓在 0.5 以下**，而且只給 0.06 秒以內的東西。
      超過泛光門檻的大面積光就是魔法，這條是前兩版用血換來的。
   3. **色相刻意避開敵人。** 敵人的「支援」是藍 `0x4f9dff`，
      這裡用青綠 `0x5ff0d8` —— 往綠偏一階，靠近玩家的識別綠。
      ⚠️ 這仍是三個候選裡撞色風險最高的一個，使用者已知會。

   ## 記憶點

   **放射針片**。命中的瞬間六片針從裝甲上炸開，長度不一、瞬間消失。 */

export var ION: any = {
  id: 'ion',
  name: 'D · 硬邊能量',
  tag: '冷色，但每一片都有銳利輪廓（有邊界就不是魔法）',
  c: { hot: 0xffffff, acc: 0x5ff0d8, ink: 0x0a2a2a, chip: 0x9aa6b8 },
  /* 這個方向需要一點泛光才有能量感，但門檻不能降 ——
     降門檻會讓所有東西一起衝回來（`weapon-fx.ts` 檔頭那條教訓）。
     所以強度還原到場景值、門檻維持高位，只有白核心過得去。 */
  bloom: { strength: 1.15, radius: 0.65, threshold: 0.70 },
  punch: { shake: 0.016, critStop: 0.028, camPush: 0.17 },
  bullet: { c: 0x5ff0d8, w: 0.11, len: 3.2, ink: 0x0a2a2a },
  muzzle: muzzle,
  impact: impact
};

/** 一片針。躺平在地面朝向 `a`，兩端尖、極短命。 */
function needle(x: number, y: number, z: number, a: number, len: number,
  color: number, speed: number, life: number) {
  spawnQuad(texNeedle(), x, y, z, FLAT, yawOf(a), 0, {
    c: color, s: len, s1: len * rnd(1.2, 1.7), ar: 0.16,
    t: life, op: 0.5, pow: 2.0, ease: 1.6,
    vx: Math.sin(a) * speed, vz: Math.cos(a) * speed, drag: 3.2
  });
}

function muzzle(x: number, y: number, z: number, ang: number, power: number) {
  var big = power > 0.7;
  if (!take(big ? 9 : 6)) return;
  var s = 1.4 + power * 3.0;
  // ① 六角白核。加法但極小、極短 —— 唯一允許過泛光門檻的東西
  spawnSprite(texPolyFill(6), x, y, z, {
    c: ION.c.hot, s: s * 0.85, s1: s * 0.3, t: 0.05, op: 0.9, pow: 1.3, ease: 1.4,
    rot: rnd(0, 6.283)
  });
  /* ② 槍口的六角帶：一般混色，所以它有實體、會遮住背後。
     這一層是「量體」，而量體不靠亮度是這一版跟前兩版最大的差別。 */
  spawnSprite(texPolyBand(6, 0.22), x, y, z, {
    normal: true, c: ION.c.acc, s: s * 0.55, s1: s * 2.0, t: 0.09,
    op: 0.75, pow: 2.2, ease: 2.5, rot: rnd(0, 6.283)
  });
  // ③ 沿射向的三片針（電漿被推出去）
  for (var i = 0; i < 3; i++) {
    needle(x, y, z, ang + rnd(-0.30, 0.30), s * rnd(0.9, 1.6), ION.c.acc, 9, 0.07);
  }
  // ④ 反衝：兩片往後
  needle(x, y, z, ang + Math.PI + rnd(-0.5, 0.5), s * 0.7, ION.c.hot, 7, 0.06);
  if (!big) return;
  for (var j = 0; j < 3; j++) needle(x, y, z, ang + Math.PI + rnd(-0.9, 0.9), s, ION.c.acc, 14, 0.09);
}

function impact(x: number, y: number, z: number, ang: number, crit: boolean) {
  if (!take(crit ? 13 : 8)) return;
  var k = crit ? 1.6 : 1;
  // ① 白核
  spawnSprite(texPolyFill(6), x, y, z, {
    c: ION.c.hot, s: 1.3 * k, s1: 0.4 * k, t: 0.055, op: 0.85, pow: 1.4, ease: 1.5,
    rot: rnd(0, 6.283)
  });
  // ② 六角帶：一般混色的量體
  spawnSprite(texPolyBand(6, 0.20), x, y, z, {
    normal: true, c: ION.c.acc, s: 0.8 * k, s1: 3.0 * k, t: 0.12,
    op: 0.7, pow: 2.2, ease: 2.6, rot: rnd(0, 6.283)
  });
  /* ③ **放射針片 —— 這個風格的記憶點。**
     六片針從命中點炸開，長度不一（等長會讀成星星，那是符號不是撞擊）。
     偏向子彈來向的反側，讀得出力的方向。 */
  var n = crit ? 8 : 5;
  for (var i = 0; i < n; i++) {
    var a = ang + Math.PI + (i / n - 0.5) * (crit ? 4.0 : 2.6) + rnd(-0.2, 0.2);
    needle(x, y, z, a, rnd(1.4, 3.2) * k, i % 3 === 0 ? ION.c.hot : ION.c.acc, 13, rnd(0.07, 0.12));
  }
  if (!crit) return;
  // ④ 暴擊：第二圈更大更慢的帶，讀成衝擊有厚度
  spawnSprite(texPolyBand(6, 0.10), x, y, z, {
    normal: true, c: ION.c.hot, s: 1.4, s1: 5.4, t: 0.20, op: 0.45, pow: 2.6, ease: 2.8,
    rot: rnd(0, 6.283)
  });
}
